import React, { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import * as AnserInputValidation from '../../../../../shared/inputValidation';
import { Formik, Form, Field } from 'formik';
import { Alert, Box, Typography, Button, Grid } from '@mui/material';
import { PhotoUpload } from '../../../../Shared/PhotoUploadComponents/PhotoUpload';
import { TextInputFormField } from '../../../../Shared/TextInputFormField';
import { GENDER, US_STATES } from '../../../../../shared/constants';
import SelectFormField from '../../../../Shared/SelectFormField';
import MaskedInput from '../../../../Shared/MaskedInput';
import ProgressBar from '../../../../Shared/ProgressBar';
import { ReactComponent as AssignUser } from '../../../../../images/icons/assign_user.svg';
import { ReactComponent as NoEmailWarningIcon } from '../../../../../images/icons/adding_user_failed.svg';
import { ProfileAvatar } from '../../../../Shared/PhotoUploadComponents/ProfileAvatar';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import { getParentFacilities } from '../../../../../store/facility/actionCreators';
import {
  darkPurple,
  lightBlack,
  selectFieldStyles,
  textFieldStyles,
} from '../../../../../mainTheme';

export const NewPatientForm = ({
  onAdd,
  onCancel,
  isFromFacility,
  isFacilityAdmin,
  setCurrentStep,
  initialValues,
  requirePhone,
  prefillValues,
}: {
  onAdd: (values: any) => void;
  onCancel: () => void;
  isFromFacility?: boolean;
  isFacilityAdmin?: boolean;
  setCurrentStep: (step: string) => void;
  initialValues?: {
    facility_id?: number;
    facility_location_id?: number;
  };
  // Server flagged a possible duplicate (409 action=get_phone): phone becomes
  // required so the backend can verify this is a different person
  requirePhone?: boolean;
  // Full form values to restore after the form unmounted (e.g. while the
  // add request was in flight and then failed with get_phone)
  prefillValues?: any;
}) => {
  const dispatch = useDispatch();
  const { dashboardUserInfo } = useSelector(
    (state: RootState) => state.dashboardUser,
  );
  const { parentFacilities, isLoading: isFacilityLoading } = useSelector(
    (state: RootState) => state.facility,
  );

  // Validation schema — email, phone and gender are optional (format-checked
  // when provided); submitting without an email triggers the warning step
  const validationSchema = Yup.object({
    first_name: Yup.string()
      .matches(
        /^[A-Za-z ]+$/,
        'First name must contain only alphabetical characters',
      )
      .required('First name is required'),
    last_name: Yup.string()
      .matches(
        /^[A-Za-z ]+$/,
        'Last name must contain only alphabetical characters',
      )
      .required('Last name is required'),
    email: Yup.string().email('Please enter a valid email address'),
    address1: Yup.string().test(
      'address-required',
      'Address is required',
      function (value) {
        const { city, state, zipcode } = this.parent;
        if (!!city || !!state || !!zipcode) return !!value;
        return true;
      },
    ),
    city: Yup.string().test(
      'city-required',
      'City is required',
      function (value) {
        const { address1, state, zipcode } = this.parent;
        if (!!address1 || !!state || !!zipcode) return !!value;
        return true;
      },
    ),
    state: Yup.string().test(
      'state-required',
      'State is required',
      function (value) {
        const { address1, city, zipcode } = this.parent;
        if (!!address1 || !!city || !!zipcode) return !!value;
        return true;
      },
    ),
    zipcode: Yup.string().test(
      'zipcode-required',
      'Zip code is required',
      function (value) {
        const { address1, city, state } = this.parent;
        if (!!address1 || !!city || !!state) return !!value;
        return true;
      },
    ),
    phone: requirePhone
      ? Yup.string()
          .required('Phone number is required')
          .matches(/^\(\d{3}\) \d{3}-\d{4}$/, {
            message: 'Phone number is not valid',
          })
      : Yup.string().matches(/^\(\d{3}\) \d{3}-\d{4}$/, {
          message: 'Phone number is not valid',
          excludeEmptyString: true,
        }),
    date_of_birth: Yup.string()
      .required('Date of birth is required')
      .test('match', 'Please fill in valid date of birth', function (value) {
        return value
          ? AnserInputValidation.validateDateOfBirth(
              value.slice(value.length - 4),
              value.substr(0, 2),
              value.substr(3, 5),
            )
            ? true
            : false
          : false;
      }),
    gender_id: Yup.string(),
    ...(isFacilityAdmin
      ? {
          facility_location_id: Yup.string().required(
            'Facility location is required',
          ),
        }
      : {
          facility_id: Yup.string().when(['isFromFacility'], {
            is: (isFromFacility: boolean) => isFromFacility === true,
            then: (schema) => schema.required('Facility is required'),
          }),
          facility_location_id: Yup.string().when(
            ['isFromFacility', 'facility_id'],
            {
              is: (isFromFacility: boolean, facilityId: string) => {
                return isFromFacility && !!facilityId;
              },
              then: (schema) =>
                schema.required('Facility location is required'),
              otherwise: (schema) => schema,
            },
          ),
        }),
  });

  const PHOTO_STATES = {
    NONE: 'none',
    UPLOAD: 'upload',
  };

  const [photoState, setPhotoState] = useState(PHOTO_STATES.NONE);
  const [photoData, setPhotoData] = useState<{
    blob: Blob | null;
    previewUrl: string | null;
  }>({
    blob: null,
    previewUrl: null,
  });
  const [locations, setLocations] = useState<
    Array<{ label: string; value: number }>
  >([]);
  const [savedFormValues, setSavedFormValues] = useState<any | null>(null);
  // Values held while the no-email warning prompt is shown
  const [pendingValues, setPendingValues] = useState<any | null>(null);

  const submitPatient = (values: any) => {
    const { email, phone, gender_id, ...rest } = values;
    onAdd({
      ...rest,
      ...(email ? { email } : {}),
      ...(phone ? { phone } : {}),
      ...(gender_id ? { gender_id } : {}),
      photo: photoData.blob,
    });
  };

  // Only fetch parent facilities if not facility admin
  useEffect(() => {
    if (isFromFacility && !isFacilityAdmin && !initialValues?.facility_id) {
      dispatch(getParentFacilities());
    }
  }, [isFromFacility, isFacilityAdmin, dispatch, initialValues?.facility_id]);

  // Set locations for facility admin from dashboardUserInfo
  useEffect(() => {
    if (isFacilityAdmin && dashboardUserInfo?.facility_id) {
      setLocations([
        {
          label: dashboardUserInfo.facility_name ?? '',
          value: dashboardUserInfo.facility_id,
        },
      ]);
    } else if (initialValues?.facility_id && parentFacilities) {
      const facility = parentFacilities.find(
        (f) => f.parent_facility_id === initialValues.facility_id,
      );
      if (facility) {
        const locationOptions =
          facility.locations?.map((loc) => ({
            label: loc.name,
            value: loc.id,
          })) || [];
        setLocations(locationOptions);
      }
    }
  }, [
    isFacilityAdmin,
    dashboardUserInfo,
    initialValues?.facility_id,
    parentFacilities,
  ]);

  const facilityOptions = useMemo(
    () =>
      parentFacilities?.map((facility) => ({
        label: facility.parent_facility_name,
        value: facility.parent_facility_id,
      })) || [],
    [parentFacilities],
  );

  const states_list = useMemo(() => {
    if (US_STATES.length < 0) return [];
    return US_STATES.map((item) => {
      return { label: item.name, value: item.id };
    });
  }, []);

  const genderData = useMemo(() => {
    return [
      { label: 'Female', value: GENDER.FEMALE },
      { label: 'Male', value: GENDER.MALE },
      { label: 'Other', value: GENDER.DECLINE_TO_SAY },
    ];
  }, []);

  // Handle opening photo upload flow
  const handleAddPhoto = (currentValues?: any) => {
    if (currentValues) setSavedFormValues(currentValues);
    setPhotoState(PHOTO_STATES.UPLOAD);
    setCurrentStep('upload_photo');
  };

  // Handle photo selected (moves to preview)
  const handlePhotoSelected = (imageBlob: Blob, previewUrl: string) => {
    setPhotoData({
      blob: imageBlob,
      previewUrl: previewUrl,
    });
    setPhotoState(PHOTO_STATES.NONE);
    setCurrentStep('new_patient');
  };

  // Render photo-related content based on state
  const renderPhotoContent = () => {
    switch (photoState) {
      case PHOTO_STATES.UPLOAD:
        return (
          <PhotoUpload
            onBack={() => {
              setPhotoState(PHOTO_STATES.NONE);
              setCurrentStep('new_patient');
            }}
            onSave={handlePhotoSelected}
            initialPhoto={photoData.previewUrl}
          />
        );
      default:
        return null;
    }
  };

  // Initial values for facility admin
  const initialFacilityLocationId =
    isFacilityAdmin && dashboardUserInfo?.facility_id
      ? dashboardUserInfo.facility_id
      : initialValues?.facility_location_id || '';

  if (pendingValues) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <NoEmailWarningIcon />

        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '22px',
            lineHeight: '30px',
            letterSpacing: '0.36px',
            color: darkPurple,
          }}
        >
          No email address provided
        </Typography>

        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '16px',
            lineHeight: '22px',
            letterSpacing: '0.2px',
            color: lightBlack,
          }}
        >
          Without an email address, this owner will not be able to log in to
          their account.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            mt: '40px',
            width: '100%',
          }}
        >
          <Button
            variant="text"
            color="secondary"
            onClick={() => setPendingValues(null)}
            sx={{
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: 700,
              lineHeight: '18px',
              letterSpacing: '0.2px',
              height: '44px',
              width: '180px',
            }}
          >
            Add email
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              const values = pendingValues;
              setPendingValues(null);
              submitPatient(values);
            }}
            sx={{
              textAlign: 'center',
              fontSize: '14px',
              fontWeight: 700,
              lineHeight: '18px',
              letterSpacing: '0.2px',
              height: '44px',
              width: '180px',
            }}
          >
            Continue without email
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <>
      {photoState !== PHOTO_STATES.NONE ? (
        renderPhotoContent()
      ) : (
        <Formik
          initialValues={
            savedFormValues ||
            prefillValues || {
              first_name: '',
              last_name: '',
              email: '',
              address1: '',
              address2: '',
              city: '',
              state: '',
              zipcode: '',
              phone: '',
              date_of_birth: '',
              gender_id: '',
              facility_id: initialValues?.facility_id || '',
              facility_location_id: initialFacilityLocationId,
              isFromFacility: isFromFacility || false,
            }
          }
          validationSchema={validationSchema}
          onSubmit={(values) => {
            // requirePhone means this is a retry — the no-email warning was
            // already acknowledged on the first submission
            if (!values.email && !requirePhone) {
              // Keep the entered values so "Add email" returns to a filled form
              setSavedFormValues(values);
              setPendingValues(values);
              return;
            }
            submitPatient(values);
          }}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({
            handleSubmit,
            isValid,
            dirty,
            setFieldValue,
            validateForm,
            values,
          }) => {
            const handleFacilityChange = async (
              event: React.ChangeEvent<{ value: unknown }>,
            ) => {
              const facilityId = event.target.value as number;

              await setFieldValue('facility_id', facilityId);

              const facility = parentFacilities?.find(
                (f) => f.parent_facility_id === facilityId,
              );
              const locationOptions =
                facility?.locations?.map((loc) => ({
                  label: loc.name,
                  value: loc.id,
                })) || [];

              setLocations(locationOptions);
              // Only clear location if not provided in initial values
              if (!initialValues?.facility_location_id) {
                await setFieldValue('facility_location_id', '');
              }
              validateForm();
            };

            return (
              <Form onSubmit={handleSubmit}>
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <ProgressBar
                    currentStep={
                      isFacilityAdmin ? 1 : initialValues?.facility_id ? 1 : 2
                    }
                    totalSteps={2}
                  />
                  <AssignUser />
                  <Typography>Add new patient details</Typography>

                  {requirePhone && (
                    // Deliberately vague: must not reveal that a matching
                    // patient record exists (inference/enumeration risk)
                    <Alert severity="info">
                      More information is needed to add this patient. Please
                      provide a phone number to continue.
                    </Alert>
                  )}

                  <ProfileAvatar
                    photo={photoData.previewUrl}
                    onAddPhoto={() => handleAddPhoto(values)}
                  />
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Field
                          label="First name"
                          name="first_name"
                          type="text"
                          placeholder="First name"
                          component={TextInputFormField}
                          sx={textFieldStyles}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Field
                          label="Last name"
                          name="last_name"
                          type="text"
                          placeholder="Last name"
                          component={TextInputFormField}
                          sx={textFieldStyles}
                        />
                      </Grid>
                    </Grid>
                    <Field
                      label="Email address (optional)"
                      name="email"
                      type="text"
                      placeholder="Email address (optional)"
                      component={TextInputFormField}
                      sx={textFieldStyles}
                    />
                    {/* Facility admin: show only facility location, read-only */}
                    {isFacilityAdmin ? (
                      <Field
                        label="Facility location"
                        name="facility_location_id"
                        placeholder="Facility location"
                        component={SelectFormField}
                        options={locations}
                        useNewStyles
                        sx={{
                          ...selectFieldStyles,
                          '& .MuiSelect-select': {
                            pointerEvents: 'none',
                            color: lightBlack,
                          },
                        }}
                      />
                    ) : (
                      isFromFacility &&
                      !initialValues?.facility_location_id && (
                        <>
                          <Field
                            label="Facility name"
                            name="facility_id"
                            placeholder="Select facility"
                            component={SelectFormField}
                            options={facilityOptions}
                            onChange={handleFacilityChange}
                            disabled={isFacilityLoading}
                            useNewStyles
                            sx={{
                              ...selectFieldStyles,
                              ...(!!initialValues?.facility_id && {
                                '& .MuiSelect-select': {
                                  pointerEvents: 'none',
                                  color: lightBlack,
                                },
                              }),
                            }}
                          />
                          <Field
                            label="Facility location"
                            name="facility_location_id"
                            placeholder="Select location"
                            component={SelectFormField}
                            options={locations}
                            useNewStyles
                            sx={selectFieldStyles}
                          />
                        </>
                      )
                    )}
                    <Field
                      label="Address"
                      name="address1"
                      placeholder="Address"
                      component={TextInputFormField}
                      sx={textFieldStyles}
                    />
                    {!isFromFacility && (
                      <Field
                        label="Address 2 (optional)"
                        name="address2"
                        placeholder="Address 2 (optional)"
                        component={TextInputFormField}
                        sx={textFieldStyles}
                      />
                    )}
                    {isFromFacility && (
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Field
                            label="Wing"
                            name="wing"
                            type="text"
                            placeholder="Wing"
                            component={TextInputFormField}
                            sx={textFieldStyles}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Field
                            label="Room number"
                            name="room_number"
                            type="text"
                            placeholder="Room number"
                            component={TextInputFormField}
                            sx={textFieldStyles}
                          />
                        </Grid>
                      </Grid>
                    )}
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Field
                          label="City"
                          name="city"
                          placeholder="City"
                          component={TextInputFormField}
                          sx={textFieldStyles}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Field
                          label="State"
                          name="state"
                          placeholder="State"
                          component={SelectFormField}
                          options={states_list}
                          useNewStyles
                          sx={selectFieldStyles}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Field
                          label="Zip code"
                          name="zipcode"
                          placeholder="Zip code"
                          component={TextInputFormField}
                          sx={textFieldStyles}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Field
                          label={
                            requirePhone
                              ? 'Phone number'
                              : 'Phone number (optional)'
                          }
                          name="phone"
                          format="(###) ###-####"
                          placeholder={
                            requirePhone
                              ? 'Phone number'
                              : 'Phone number (optional)'
                          }
                          component={MaskedInput}
                          sx={textFieldStyles}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Field
                          label="Date of birth"
                          name="date_of_birth"
                          format="##/##/####"
                          mask={['M', 'M', 'D', 'D', 'Y', 'Y', 'Y', 'Y']}
                          placeholder="MM/DD/YYYY"
                          component={MaskedInput}
                          sx={textFieldStyles}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Field
                          label="Gender (optional)"
                          name="gender_id"
                          placeholder="Gender (optional)"
                          component={SelectFormField}
                          options={genderData}
                          useNewStyles
                          sx={selectFieldStyles}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mt: 3,
                    }}
                  >
                    <Button
                      variant="text"
                      color="secondary"
                      onClick={onCancel}
                      sx={{
                        textAlign: 'center',
                        fontSize: '14px',
                        fontWeight: 700,
                        lineHeight: '18px',
                        letterSpacing: '0.2px',
                        height: '44px',
                        width: '180px',
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      type="submit"
                      disabled={!isValid || !dirty}
                      sx={{
                        textAlign: 'center',
                        fontSize: '14px',
                        fontWeight: 700,
                        lineHeight: '18px',
                        letterSpacing: '0.2px',
                        height: '44px',
                        width: '180px',
                      }}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              </Form>
            );
          }}
        </Formik>
      )}
    </>
  );
};
